/* ══ v11 (W2): the PORTFOLIO — one level above the programme ══════════════════════════
   v10.5 made the portfolio a FOLDER of files and asked the launcher to enumerate it.
   v11 makes it a LIST INSIDE ONE FILE, so the list needs no launcher call at all — it is
   `__DB__.programmes`, already in memory. Selecting a programme became a pure in-memory
   act: no file handle, no picker, no permission prompt, nothing to fail.

   ⚠ Retired IN THIS VERSION with that change (build standard 3 — superseded code goes in
   the version that supersedes it): `openProgramme` and the per-file handle store,
   mechanism-A creation (`pfCreateProgramme`), the ◆1 slug naming, and §4b's amber FILE
   rows. Fork DETECTION did not go away — it moved to the launcher's folder sentry (W3),
   whose banner renders through `_dataFileBannersHtml`. */

/* The last programme you looked at, remembered per USER (a server preference) rather than in
   the shared portfolio: it is one person's view state, and writing it into shared data on every
   switch would make it change under colleagues for nothing. v18 moved it from IndexedDB, which
   held it per browser profile, to the user's server-side preferences. */
function _saveLastProgramme(id) { return _srvSetPref('global', 'lastProgramme', String(id || '')); }
function _loadLastProgramme() { return Promise.resolve(_srvPref('global', 'lastProgramme') || null); }
/* v12.1 (W2, ◆E): where he last SAVED from. Recorded on a SUCCESSFUL save only, so a session he
   never saved leaves no trace and reopens on the portfolio. */
function _saveLastView(h) { return _srvSetPref('global', 'lastView', String(h || '')); }
function _loadLastView() { return Promise.resolve(_srvPref('global', 'lastView') || null); }

function _progProjectCount(pid) {
  const db = window.__DB__;
  const all = (db && Array.isArray(db.projects)) ? db.projects : [];
  return all.filter(c => c && !c.archived && c.programmeId === pid).length;
}
/* The same definition the landing page uses — a project whose STATUS dot is red or
   amber. Archived projects are excluded, so the two numbers cannot disagree. */
function _progAttention(pid) {
  const db = window.__DB__;
  const all = (db && Array.isArray(db.projects)) ? db.projects : [];
  return all.filter(c => c && !c.archived && c.programmeId === pid
    && c.appMeta && c.appMeta.dashboard && c.appMeta.dashboard.rag
    && ['red', 'amber'].includes(c.appMeta.dashboard.rag.level)).length;
}
/* Switch programmes. In memory, instantly — and it clears the active PROJECT, because a
   project id from the programme you just left would otherwise still be current. */
function selectProgramme(id) {
  const P = _programmes().find(p => p && p.id === id);
  /* v12.1 (W1): an archived programme cannot be opened — unarchive it from the row menu */
  if (!P || P.archived) return;
  if (!_progressPermitRoute('#/overview')) return;
  activeProgrammeId = id;
  _saveLastProgramme(id);
  activeProjectId = null;
  gotoOverview();
}
/* P4 disclosure belongs to this Portfolio visit, never to saved programme/project data. */
let _pfExpandedProgrammeIds = new Set(), _pfExpandedFolderKey = null;
function _pfResetExpansion() { _pfExpandedProgrammeIds.clear(); _pfExpandedFolderKey = null; }
function _pfLiveChildren(P) {
  if (!P || P.del || P.archived) return [];
  return _listOrderOrdered(_listOrderAll('project', P.id).filter(function (c) {
    return !c.archived && c.programmeId === P.id;
  }), 'project', P.id);
}
function _pfPruneExpansion() {
  const folder = _listOrderFolderKey();
  if (_pfExpandedFolderKey && folder && _pfExpandedFolderKey !== folder) _pfExpandedProgrammeIds.clear();
  if (folder) _pfExpandedFolderKey = folder;
  _pfExpandedProgrammeIds.forEach(function (id) {
    const P = _listOrderAll('programme', '').find(function (p) { return p.id === id; });
    if (!_pfLiveChildren(P).length) _pfExpandedProgrammeIds.delete(id);
  });
}
function _pfRowEvent(event, row) {
  if (!row.isConnected || !document.body.classList.contains('view-portfolio') || _pfPane !== 'programmes') return false;
  const folder = _listOrderFolderKey();
  if (_pfExpandedFolderKey && folder && _pfExpandedFolderKey !== folder) { _pfPruneExpansion(); renderPortfolio(); return false; }
  const interactive = event.target.closest('button,a,input,select,textarea,[role="button"]');
  if (interactive && interactive !== row) return false;
  if (event.type === 'keydown') {
    if (event.key !== 'Enter' && event.key !== ' ') return false;
    event.preventDefault();
  }
  return true;
}
function _pfProgrammeActivate(event, row) {
  if (!_pfRowEvent(event, row)) return;
  const P = _listOrderAll('programme', '').find(function (p) { return p.id === row.dataset.id; });
  if (P && !P.archived) selectProgramme(P.id);
}
function _pfProjectActivate(event, row) {
  if (!_pfRowEvent(event, row)) return;
  const P = _listOrderAll('programme', '').find(function (p) { return p.id === row.dataset.programmeId; });
  const C = _pfLiveChildren(P).find(function (c) {
    return c.id === row.dataset.projectId && String(c.uid || '') === row.dataset.projectUid;
  });
  if (C) gotoProject(C.id);
}
function pfToggleProgrammeProjects(event, button) {
  event.stopPropagation();
  if (!button.isConnected || !document.body.classList.contains('view-portfolio') || _pfPane !== 'programmes' || _hdrQuery.trim()) return;
  const folder = _listOrderFolderKey();
  if (_pfExpandedFolderKey && folder && _pfExpandedFolderKey !== folder) { _pfPruneExpansion(); renderPortfolio(); return; }
  const id = button.dataset.programmeId;
  const P = _listOrderAll('programme', '').find(function (p) { return p.id === id; });
  if (!_pfLiveChildren(P).length) return;
  if (_pfExpandedProgrammeIds.has(id)) _pfExpandedProgrammeIds.delete(id); else _pfExpandedProgrammeIds.add(id);
  renderPortfolio();
  const next = document.querySelector('#portfolioView .pf-disclosure[data-programme-id="' + CSS.escape(id) + '"]');
  if (next) next.focus({ preventScroll: true });
}
function _pfProgrammeGroupHtml(P) {
  const children = _pfLiveChildren(P), open = _pfExpandedProgrammeIds.has(P.id);
  const blockId = 'pfProjects-' + encodeURIComponent(P.id);
  return `<div class="pf-programme-group" data-programme-id="${_escapeHtml(P.id)}">`
    + _pfRowHtml(P, '', { available: !!children.length, open, blockId })
    + `<div class="pf-children" id="${_escapeHtml(blockId)}" data-programme-id="${_escapeHtml(P.id)}"${open ? '' : ' hidden'}>`
    + (open ? children.map(function (C) { return _pfProjectHitHtml(C, P, false); }).join('') : '') + `</div></div>`;
}
function _pfRowHtml(P, extraClass, disclosure) {
  /* v16.2: `const here = P.id === activeProgrammeId` lived here and fed the "Open now"
     pill ONLY (verified: one declaration, one use). The pill went, so it goes too —
     leaving it would be dead code. ⚠ `arch` below STAYS: it drives the row class and the
     archived row's missing select handler, which are unrelated. */
  const named = !!(P.name && P.name.trim());
  const arch = !!P.archived;
  const n = _progProjectCount(P.id), att = _progAttention(P.id);
  const counts = n + ' project' + (n === 1 ? '' : 's')
    + (att ? ' · ' + att + ' need' + (att === 1 ? 's' : '') + ' attention' : '');
  const lvl = (P.rag && P.rag.level) || '';
  const eid = _escapeHtml(P.id);
  /* v13.1 (A56): was `eid.replace(/'/g, "\\'")` — dead code, because _escapeHtml had
     already turned every apostrophe into &#39;. See _jsInAttr for why that mattered. */
  const toggle = disclosure ? (disclosure.available
    ? `<button type="button" class="pf-disclosure" data-programme-id="${eid}" aria-expanded="${disclosure.open}"`
      + ` aria-controls="${_escapeHtml(disclosure.blockId)}" aria-label="${disclosure.open ? 'Hide' : 'Show'} projects in ${_escapeHtml(named ? P.name : 'Unnamed programme')}"`
      + ` onclick="pfToggleProgrammeProjects(event,this)"><span aria-hidden="true">${disclosure.open ? '⌄' : '›'}</span></button>`
    : `<span class="pf-disclosure-slot" aria-hidden="true"></span>`) : '';
  /* v12.1 (W1): an ARCHIVED row gets no select handler — it cannot be opened, only
     unarchived from its own menu. The ⋯ menu stops propagation, so on a live row it
     never triggers selectProgramme. */
  return `<div class="pf-row${arch ? ' pf-arch' : ''}${extraClass ? ' ' + extraClass : ''}${disclosure ? ' pf-with-disclosure' : ''}" data-id="${eid}"`
    + (arch ? `` : ` role="button" tabindex="0"`
        + ` onclick="_pfProgrammeActivate(event,this)" onkeydown="_pfProgrammeActivate(event,this)"`)
    + `>` + toggle
    + `<span class="pf-dot${lvl ? ' rag-' + lvl : ''}" title="Programme status: ${lvl || 'not set'}"></span>`
    /* v16.5 (d)(f): the quotable id, then the name, then the lifecycle status. ⚠ The status is
       WORDED AS A STATE and carries no chevron, no arrow and no imperative - v16.2's "Open now"
       pill was reported as confusing precisely because imperative wording on a descriptive badge
       reads as a control you cannot find the switch for. It is set from the row menu. */
    + `<div class="pf-main"><div class="pf-name${named ? '' : ' pf-unnamed'}">`
    +   (_dispId('PRGM', P) ? `<span class="disp-id">${_escapeHtml(_dispId('PRGM', P))}</span>` : '')
    +   `${_escapeHtml(named ? P.name : 'Unnamed programme')}`
    +   (P.lifecycle ? `<span class="lc-badge lc-${_escapeHtml(P.lifecycle)}">${_escapeHtml(_progLifecycleLabel(P.lifecycle))}</span>` : '')
    +   `</div>`
    + (named ? '' : `<div class="pf-file">Open it to give it a name.</div>`)
    + `</div><div class="pf-meta">`
    + `<span>${_escapeHtml(counts)}</span>`
    + _listOrderMoveHtml('programme', '', P)
    + `<div class="pm-menu-wrap">`
    +   `<button class="ov-card-menu-btn pm-menu-btn pf-menu-btn" data-id="${eid}" onclick="toggleCardMenu(event, this.dataset.id, 'programme')" title="Manage programme" aria-label="Manage programme">`
    +     `<svg viewBox="0 0 16 16" width="15" height="15"><circle cx="3" cy="8" r="1.45"/><circle cx="8" cy="8" r="1.45"/><circle cx="13" cy="8" r="1.45"/></svg>`
    +   `</button><div class="ov-card-menu" id="progMenu-${eid}" hidden></div>`
    + `</div>`
    + `</div></div>`;
}
